import { Link } from "react-router-dom";
import {
  Target, Eye, Heart, Users, ShieldCheck, Zap,
  Building2, TrendingUp, ArrowRight, MapPin, BadgeIndianRupee,
} from "lucide-react";

const stats = [
  { value: "12,500+", label: "Verified PGs",   Icon: Building2 },
  { value: "22,000+", label: "Happy Renters",  Icon: Users },
  { value: "50+",     label: "Cities Covered", Icon: MapPin },
  { value: "₹0",      label: "Brokerage",      Icon: BadgeIndianRupee },
];

const values = [
  { Icon: ShieldCheck, title: "Trust First",       description: "Every listing goes through a 50-point safety and hygiene audit before it goes live. No fake photos, no hidden costs." },
  { Icon: Heart,       title: "Renter-Centric",    description: "We build features renters actually need — transparent pricing, real reviews, and instant booking. Always." },
  { Icon: Zap,         title: "Zero Friction",      description: "From search to move-in, we remove every unnecessary step. No brokers, no site visits needed, no paperwork headaches." },
  { Icon: TrendingUp,  title: "Owner Empowerment", description: "We give PG owners professional tools — smart listings, digital KYC, occupancy dashboards — so they can scale without middlemen." },
];

const milestones = [
  { year: "2022", title: "The Idea",              text: "Frustrated with broker fees and fake listings, our founders set out to fix PG hunting in India." },
  { year: "2023", title: "First 1,000 Listings",  text: "Launched in Delhi NCR with a focus on student hubs like Karol Bagh, Mukherjee Nagar, and Old Rajinder Nagar." },
  { year: "2024", title: "Trust Shield Launched", text: "Introduced our proprietary 50-point safety audit — the AapkaPG Trust Shield — and hit 5,000+ verified PGs." },
  { year: "2025", title: "Scaling Nationwide",    text: "Expanded to 50+ cities, onboarded 12,500+ PGs, and crossed 22,000 happy renters with zero brokerage." },
];

const team = [
  { name: "Rahul Sharma", role: "Co-Founder & CEO",       bio: "Ex-Zomato. Obsessed with eliminating information asymmetry in India's rental market." },
  { name: "Priya Mehta",  role: "Co-Founder & CPO",       bio: "Ex-Housing.com. Designs products that put renters first and owners in control." },
  { name: "Arjun Kapoor", role: "Head of Trust & Safety", bio: "10+ years in property audits. The person behind the 50-point Trust Shield." },
  { name: "Sneha Reddy",  role: "Head of Growth",         bio: "Community builder. Grew AapkaPG from 0 to 50 cities through grassroots marketing." },
];

export default function About() {
  return (
    <div className="abt-root">

      {/* Hero */}
      <section className="abt-hero">
        <div className="abt-hero__bg" />
        <div className="abt-hero__glow" />
        <div className="abt-container abt-hero__content">
          <span className="abt-badge"><span className="abt-badge__dot" /> Our Story</span>
          <h1 className="abt-fluid-hero abt-hero__title">
            We're making PG hunting <span className="abt-primary">human again</span>
          </h1>
          <p className="abt-fluid-lg abt-hero__sub">
            AapkaPG.com is a product of Fast Management Service Private Limited. No brokers. No fake listings. No hidden charges. Just verified PGs,
            transparent pricing, and a move-in experience that respects your time and money.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="abt-stats-section">
        <div className="abt-container abt-stats-grid">
          {stats.map((s) => (
            <div key={s.label} className="abt-stat">
              <div className="abt-icon-box abt-icon-box--md abt-stat__icon">
                <s.Icon className="abt-icon" />
              </div>
              <div className="abt-fluid-3xl abt-fw-800 abt-stat__value">{s.value}</div>
              <div className="abt-stat__label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="abt-section">
        <div className="abt-container abt-mv-grid">
          <div className="abt-card abt-card--lg">
            <div className="abt-icon-box abt-icon-box--md abt-mb-sm"><Target className="abt-icon" /></div>
            <h2 className="abt-fluid-2xl abt-fw-800">Our Mission</h2>
            <p className="abt-fluid-base abt-muted abt-mt-sm">
              To make finding a PG in India as easy, transparent, and broker-free as booking a hotel room.
              We believe every student, professional, and working individual deserves a safe, affordable
              place to call home — without paying a single rupee in brokerage.
            </p>
          </div>
          <div className="abt-card abt-card--lg">
            <div className="abt-icon-box abt-icon-box--md abt-mb-sm"><Eye className="abt-icon" /></div>
            <h2 className="abt-fluid-2xl abt-fw-800">Our Vision</h2>
            <p className="abt-fluid-base abt-muted abt-mt-sm">
              A world where PG owners can fill beds without brokers, and renters can trust every listing
              they see online. We're building the infrastructure for India's ₹1.5 lakh crore PG and
              co-living market — one verified listing at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="abt-section abt-section--muted">
        <div className="abt-container">
          <div className="abt-section-header">
            <span className="abt-badge">What drives us</span>
            <h2 className="abt-fluid-3xl abt-fw-800 abt-mt-md">Our Core Values</h2>
          </div>
          <div className="abt-values-grid">
            {values.map((v) => (
              <div key={v.title} className="abt-card">
                <div className="abt-icon-box abt-icon-box--sm abt-mb-xs"><v.Icon className="abt-icon" /></div>
                <h3 className="abt-fw-700 abt-value-title">{v.title}</h3>
                <p className="abt-muted abt-value-desc">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-section-header">
            <span className="abt-badge">Our Journey</span>
            <h2 className="abt-fluid-3xl abt-fw-800 abt-mt-md">From Idea to Impact</h2>
          </div>
          <div className="abt-timeline">
            <div className="abt-timeline__line" />
            {milestones.map((m, i) => (
              <div key={m.year} className={`abt-timeline-item ${i % 2 === 1 ? "abt-timeline-item--right" : ""}`}>
                <div className="abt-timeline-dot">{m.year.slice(2)}</div>
                <div className={`abt-card abt-timeline-card ${i % 2 === 0 ? "abt-timeline-card--left" : "abt-timeline-card--right"}`}>
                  <div className="abt-primary abt-fw-700 abt-timeline-year">{m.year}</div>
                  <h3 className="abt-fw-700 abt-timeline-title">{m.title}</h3>
                  <p className="abt-muted abt-timeline-text">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="abt-section abt-section--muted">
        <div className="abt-container">
          <div className="abt-section-header">
            <span className="abt-badge">The People</span>
            <h2 className="abt-fluid-3xl abt-fw-800 abt-mt-md">Meet the Team</h2>
            <p className="abt-muted abt-team-sub">
              A small, mission-driven team obsessed with fixing India's PG ecosystem.
            </p>
          </div>
          <div className="abt-team-grid">
            {team.map((t) => (
              <div key={t.name} className="abt-card abt-team-card">
                <div className="abt-avatar">{t.name.split(" ").map(n => n[0]).join("")}</div>
                <h3 className="abt-fw-700 abt-team-name">{t.name}</h3>
                <div className="abt-primary abt-fw-600 abt-team-role">{t.role}</div>
                <p className="abt-muted abt-team-bio">{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="abt-section">
        <div className="abt-container">
          <div className="abt-cta-box">
            <h2 className="abt-fluid-3xl abt-fw-800 abt-cta-title">Ready to find your perfect PG?</h2>
            <p className="abt-cta-sub">
              Join 22,000+ renters who found verified, zero-brokerage PGs through AapkaPG.
            </p>
            <div className="abt-cta-btns">
              <Link to="/listings" className="abt-btn-primary">
                Explore PGs <ArrowRight className="abt-btn-icon" />
              </Link>
              <Link to="/contact" className="abt-btn-outline">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}